import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete } from '../utils/api';

interface FileItem {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size?: number;
  modified?: string;
}

const Files: React.FC = () => {
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadFiles();
  }, [currentPath]);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<{ files: FileItem[] }>(`/files/list?path=${encodeURIComponent(currentPath)}`);
      setFiles(data.files || []);
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки файлов');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (path: string) => {
    if (path === '..') {
      const parentPath = currentPath.split('/').slice(0, -1).join('/') || '/';
      setCurrentPath(parentPath);
    } else {
      const newPath = currentPath === '/' ? `/${path}` : `${currentPath}/${path}`;
      setCurrentPath(newPath);
    }
  };

  const handleDelete = async (file: FileItem) => {
    if (!confirm(`Удалить ${file.type === 'directory' ? 'папку' : 'файл'} "${file.name}"?`)) {
      return;
    }

    try {
      await apiDelete(`/files/delete?path=${encodeURIComponent(file.path)}`);
      loadFiles();
    } catch (err: any) {
      setError(err.message || 'Ошибка удаления');
    }
  };

  const handleRename = async () => {
    if (!selectedFile || !newName.trim()) return;

    try {
      await apiPost('/files/rename', {
        oldPath: selectedFile.path,
        newName: newName.trim()
      });
      setRenameModalOpen(false);
      setSelectedFile(null);
      setNewName('');
      loadFiles();
    } catch (err: any) {
      setError(err.message || 'Ошибка переименования');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', currentPath);

      const response = await fetch('/api/files/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Ошибка загрузки файла');
      }

      // Ждем ответ от сервера и небольшую задержку для гарантии сохранения файла
      await response.json();
      // Небольшая задержка для гарантии, что файл полностью сохранен на сервере
      await new Promise(resolve => setTimeout(resolve, 100));
      loadFiles();
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки файла');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleCreateFolder = async () => {
    const folderName = prompt('Введите название папки:');
    if (!folderName?.trim()) return;

    try {
      await apiPost('/files/create-folder', {
        path: currentPath,
        name: folderName.trim()
      });
      loadFiles();
    } catch (err: any) {
      setError(err.message || 'Ошибка создания папки');
    }
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ru-RU');
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Файлы</h1>
        <div className="flex gap-2">
          <button
            onClick={handleCreateFolder}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            📁 Создать папку
          </button>
          <label className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors cursor-pointer">
            📤 Загрузить файл
            <input
              type="file"
              className="hidden"
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Навигация по пути */}
      <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
        <button
          onClick={() => setCurrentPath('/')}
          className="hover:text-gray-900"
        >
          /
        </button>
        {currentPath !== '/' && currentPath.split('/').filter(Boolean).map((segment, index, array) => (
          <React.Fragment key={index}>
            <span>/</span>
            <button
              onClick={() => {
                const newPath = '/' + array.slice(0, index + 1).join('/');
                setCurrentPath(newPath);
              }}
              className="hover:text-gray-900"
            >
              {segment}
            </button>
          </React.Fragment>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <p>Загрузка...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Имя
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Размер
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Изменен
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentPath !== '/' && (
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleNavigate('..')}
                      className="text-blue-600 hover:text-blue-900 font-medium flex items-center gap-2"
                    >
                      📁 ..
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"></td>
                </tr>
              )}
              {files.map((file, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {file.type === 'directory' ? (
                      <button
                        onClick={() => handleNavigate(file.name)}
                        className="text-blue-600 hover:text-blue-900 font-medium flex items-center gap-2"
                      >
                        📁 {file.name}
                      </button>
                    ) : (
                      <span className="text-gray-900 flex items-center gap-2">
                        📄 {file.name}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatSize(file.size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(file.modified)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setSelectedFile(file);
                          setNewName(file.name);
                          setRenameModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Переименовать
                      </button>
                      <button
                        onClick={() => handleDelete(file)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {files.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    Папка пуста
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Модальное окно переименования */}
      {renameModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">Переименовать</h2>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              placeholder="Новое имя"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRename();
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setRenameModalOpen(false);
                  setSelectedFile(null);
                  setNewName('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Отмена
              </button>
              <button
                onClick={handleRename}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Files;

